import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import useOutsideClick from 'hooks/useOutsideClick';
import IconThreeDots from 'assets/icon-three-dots.svg';
import {
  MoreActionsMenuButton,
  MoreActionsMenuItem,
  MoreActionsMenuList,
  MoreActionsMenuWrapper
} from 'components/MoreActionsMenu/styles';

/**
 * The three-dots menu in page headers. Pages pass only the options, so every
 * menu looks and behaves the same.
 */
const MoreActionsMenu = ({options}) => {
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const visibleOptions = options.filter((option) => !option.hidden);

  useOutsideClick(wrapperRef, () => setOpen(false), open);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      // Closing removes the focused option, so keep keyboard users on the button.
      if (wrapperRef.current?.contains(document.activeElement)) {
        buttonRef.current?.focus();
      }
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!visibleOptions.length) return null;

  return (
    <MoreActionsMenuWrapper ref={wrapperRef}>
      <MoreActionsMenuButton
        ref={buttonRef}
        type='button'
        aria-label='More options'
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <img src={IconThreeDots} alt='' />
      </MoreActionsMenuButton>
      {open && (
        <MoreActionsMenuList>
          {visibleOptions.map(({label, onClick}) => (
            <MoreActionsMenuItem
              key={label}
              type='button'
              onClick={() => {
                // The option disappears with the menu; keep focus on the button so
                // keyboard users don't lose their place (modals return focus here).
                buttonRef.current?.focus();
                setOpen(false);
                onClick();
              }}
            >
              {label}
            </MoreActionsMenuItem>
          ))}
        </MoreActionsMenuList>
      )}
    </MoreActionsMenuWrapper>
  );
};

MoreActionsMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      hidden: PropTypes.bool
    })
  ).isRequired
};

export default MoreActionsMenu;
